import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { Lightning, TrendUp, CaretLeft } from "@phosphor-icons/react";
import { useGlobalData } from "@/lib/dataStreams";
import LiveBotTerminal from "@/components/LiveBotTerminal";

function TapeHeader() {
  const navigate = useNavigate();
  return (
    <div className="relative border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl">
      <div className="max-w-[1920px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4 sm:gap-6">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors bg-slate-900 hover:bg-slate-800 border border-slate-700 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
            <CaretLeft size={16} weight="bold" /> Back
          </button>
          <Link to="/" className="flex items-center gap-3 group">
            <img src="/logo-white.png" alt="B3eye" className="h-8 w-auto" />
            <div className="hidden sm:block">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Live Trade Tape</div>
            </div>
          </Link>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right hidden sm:block">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Network Status</div>
            <div className="font-bold text-sm text-emerald-400 flex items-center justify-end gap-1.5 mt-0.5">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" /> Online
            </div>
          </div>
          <Link to="/dashboard" className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors hidden sm:block">
            Command Center
          </Link>
          <a
            href="https://app.b3eye.com"
            data-testid="tape-join-now"
            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all"
          >
            Join Now
          </a>
        </div>
      </div>
    </div>
  );
}

function TapeHero({ solPrice, exchanges }) {
  return (
    <div className="relative max-w-[1920px] w-full mx-auto px-6 md:px-10 py-12">
      <div className="flex flex-col lg:flex-row justify-between lg:items-end gap-10">
        <div>
          <span className="text-indigo-400 font-bold text-xs uppercase tracking-wider">// SOL/USD · Mainnet Aggregate</span>
          <div className="flex items-end gap-4 mt-3">
            <div
              className="font-extrabold text-6xl sm:text-8xl tracking-tighter text-slate-100"
            >
              {solPrice ? `$${solPrice.toFixed(3)}` : "—"}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap lg:justify-end gap-4 lg:gap-6">
          {Object.entries(exchanges).map(([name, price]) => (
            <div key={name} className="bg-slate-900 border border-slate-800 rounded-2xl py-5 px-6 min-w-[150px] shadow-sm">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">{name}</div>
              <div className="font-mono font-bold text-2xl text-slate-200">${price.toFixed(3)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TapeRibbon({ ticker }) {
  return (
    <div className="border-y border-slate-800 bg-indigo-500/5 py-4 relative z-10">
      {ticker.length > 0 ? (
        <Marquee speed={50} gradient={false} pauseOnHover>
          {ticker.map((t) => (
            <div key={t.id} className="flex items-center gap-3 mx-8 font-mono text-sm font-medium whitespace-nowrap">
              <span className="text-slate-400">{t.buy_ex}</span>
              <span className="text-indigo-400">→</span>
              <span className="text-slate-400">{t.sell_ex}</span>
              <span className="text-slate-200">{t.pair}</span>
              <span className="text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-md">
                <TrendUp size={14} className="inline mr-1" />+{t.spread_pct}%
              </span>
              <span className="text-slate-300">Est: <span className="text-emerald-400">${t.pnl_usd}</span></span>
              <span className="text-slate-700 ml-4 mr-1">|</span>
            </div>
          ))}
        </Marquee>
      ) : (
        <div className="text-center text-slate-500 text-xs font-bold uppercase tracking-wider animate-pulse">Streaming cross-exchange prices...</div>
      )}
    </div>
  );
}

function TapeFooter() {
  return (
    <div className="relative max-w-[1920px] w-full mx-auto px-6 md:px-10 py-8 border-t border-slate-800 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-500 mt-auto">
      <div>// Pure Frontend Architecture</div>
      <div className="flex items-center gap-2">
        <span>B3eye Infrastructure</span>
      </div>
    </div>
  );
}

export default function Tape() {
  const { solPrice, ticker, exchanges } = useGlobalData();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 relative overflow-hidden flex flex-col font-sans">
      <div className="absolute top-0 right-0 w-[800px] h-[500px] bg-indigo-500/5 blur-[150px] rounded-full pointer-events-none z-0" />
      <TapeHeader />
      <TapeHero solPrice={solPrice} exchanges={exchanges} />
      <TapeRibbon ticker={ticker} />
      <div className="flex-1 relative z-10 max-w-[1920px] w-full mx-auto px-6 md:px-10 py-10">
        <div className="h-[600px] w-full">
          <LiveBotTerminal maxItems={100} />
        </div>
      </div>
      <TapeFooter />
    </div>
  );
}
